<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <v-btn icon @click="$router.push('/incident-records')" class="mb-4">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <h2 class="d-inline-block ml-2">
          Претензии по заявке № {{ claims[0]?.requestNumber || '' }}
        </h2>
      </v-col>
    </v-row>

    <v-row v-if="loading">
      <v-col class="text-center">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
        <p class="mt-2">Загрузка данных претензий...</p>
      </v-col>
    </v-row>

    <template v-if="!loading && claims.length > 0">
      <!-- Основная информация о заявке -->
      <v-card class="mb-4">
        <v-card-title class="tableHeader tableHeaderText--text">
          Основная информация о заявке
        </v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" md="4">
              <v-text-field label="Тип груза" :value="claims[0].cargoTypeName || ''" readonly outlined
                dense></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field label="Тип заявки" :value="claims[0].claimTypeName || ''" readonly outlined
                dense></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field label="№ заявки" :value="claims[0].requestNumber || ''" readonly outlined
                dense></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field label="Дата" :value="formatDate(claims[0].requestDate)" readonly outlined
                dense></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field label="№ машины" :value="claims[0].carNumber || ''" readonly outlined dense></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field label="Водитель" :value="claims[0].driverName || ''" readonly outlined dense></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field label="Статус заявки" :value="claims[0].requestStatusName || ''" readonly outlined
                dense></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Детали претензий (список позиций) -->
      <v-card>
        <v-card-title class="tableHeader tableHeaderText--text">
          Позиции претензий ({{ claims.length }})
        </v-card-title>
        <v-card-text>
          <v-simple-table>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="tableHeader tableHeaderText--text">Артикул</th>
                  <th class="tableHeader tableHeaderText--text">Товар</th>
                  <th class="tableHeader tableHeaderText--text">Количество</th>
                  <th class="tableHeader tableHeaderText--text">Цена за ед.</th>
                  <th class="tableHeader tableHeaderText--text">Сумма</th>
                  <th class="tableHeader tableHeaderText--text">Описание</th>
                  <th class="tableHeader tableHeaderText--text">Статус</th>
                  <th class="tableHeader tableHeaderText--text d-flex justify-center align-center">Действия</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in claims" :key="index">
                  <td>{{ item.goodArticle || 'Н/Д' }}</td>
                  <td>{{ item.goodName || 'Н/Д' }}</td>
                  <td>{{ item.qty || 0 }}</td>
                  <td>{{ formatPrice(item.price) }}</td>
                  <td>{{ formatPrice((item.qty || 0) * (item.price || 0)) }}</td>
                  <td>{{ item.comment || 'Нет описания' }}</td>
                  <td>
                    <v-chip :color="getClaimStatusColor(item.status)" small dark>
                      {{ getClaimStatusName(item.status) }}
                    </v-chip>
                  </td>
                  <td>
                    <div class="d-flex justify-center align-center">
                      <ActionButton v-if="item.status === 0" icon="mdi-draw" color="green" title="Подписать"
                        align="center" :loading="isProcessing(item.id)"
                        :disabled="isProcessing(item.id) || item.status !== 0" @click="onSign(item)" />
                      <ActionButton v-if="item.status === 0" icon="mdi-cancel" color="red" title="Отклонить"
                        align="center" :loading="isProcessing(item.id)"
                        :disabled="isProcessing(item.id) || item.status !== 0" @click="onReject(item)" />
                    </div>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <!-- Итого -->
          <v-row class="mt-4">
            <v-col cols="12" class="text-right">
              <strong>
                Общая сумма претензий: {{ formatPrice(totalClaimAmount) }}
              </strong>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </template>

    <!-- Сообщение если нет претензий -->
    <v-card v-if="!loading && claims.length === 0 && !error">
      <v-card-text class="text-center">
        <v-icon large color="grey">mdi-information</v-icon>
        <p class="mt-2 grey--text">Претензии по данной заявке не найдены</p>
      </v-card-text>
    </v-card>

    <!-- Сообщение об ошибке -->
    <v-card v-if="!loading && error">
      <v-card-text class="text-center error--text">
        <v-icon large color="error">mdi-alert-circle</v-icon>
        <p class="mt-2">{{ error }}</p>
        <v-btn color="primary" @click="loadClaimData">Повторить загрузку</v-btn>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import ActionButton from '../../../components/ActionButton.vue';

export default {
  name: 'GetIncidentRecord',

  components: {
    ActionButton
  },

  data() {
    return {
      claims: [],
      loading: false,
      processingItems: [],
      error: null
    };
  },

  computed: {
    allowedUsers() {
      const route = this.$router.options.routes.find(r => r.path === '/incident-records/:id');
      return route?.meta?.allowedUsers || [];
    },
    isUserAllowed() {
      const user = this.$store.state.auth.user;
      if (!user) return false;
      const userId = parseInt(user.userID);
      return this.allowedUsers.includes(userId);
    },
    totalClaimAmount() {
      if (!this.claims || this.claims.length === 0) return 0;
      return this.claims.reduce((sum, claim) => {
        const qty = claim.qty || 0;
        const price = claim.price || 0;
        return sum + (qty * price);
      }, 0);
    }
  },

  async created() {
    if (!this.isUserAllowed) {
      this.$router.push('/');
      return;
    }
    else
      await this.loadClaimData();
  },

  methods: {
    // Загрузка данных
    async loadClaimData() {
      try {
        this.loading = true;
        this.error = null;

        const requestId = this.$route.params.id;
        if (!requestId) {
          this.error = 'ID заявки не указан';
          return;
        }

        const response = await this.axios.get(`/requests/GetRequestClaims/${requestId}`);
        this.claims = response.data;
      } catch (err) {
        console.error('Ошибка при загрузке данных претензий:', err);
        this.error = err.response?.data?.message || 'Ошибка при загрузке данных претензий';
      } finally {
        this.loading = false;
      }
    },

    // Подписание претензии
    async onSign(item) {
      try {
        if (!item.id) {
          console.error('Нет ID у позиции:', item);
          this.showError('Невозможно определить ID претензии');
          return;
        }

        this.setProcessing(item.id);

        const response = await this.axios.post(`/requests/SignIncident/${item.id}`);

        if (response.data) {
          const index = this.claims.findIndex(c => c.id === item.id);
          if (index !== -1) {
            this.claims[index].status = response.data.status;
          }
        }

        this.showSuccess('Претензия успешно подписана');

        await this.loadClaimData();

        if (this.claims.length === 0)
          this.$router.push('/incident-records')
      } catch (err) {
        console.error('Ошибка при подписании претензии:', err);
        const errorText = typeof err.response?.data === 'string'
          ? err.response.data
          : err.response?.data?.message || 'Не удалось подписать претензию';

        this.showError(errorText);
      } finally {
        this.clearProcessing(item.id);
      }
    },

    // Отклонение претензии
    async onReject(item) {
      try {
        if (!item.id) {
          console.error('Нет ID у позиции:', item);
          this.showError('Невозможно определить ID претензии');
          return;
        }

        const confirmed = this.confirmReject(item);
        if (!confirmed) return;

        this.setProcessing(item.id);

        const response = await this.axios.post(`/requests/RejectIncident/${item.id}`);

        if (response.data) {
          const index = this.claims.findIndex(c => c.id === item.id);
          if (index !== -1) {
            this.claims[index].status = response.data.status;
          }
        }

        this.showWarning('Претензия отклонена');

      } catch (err) {
        console.error('Ошибка при отклонении претензии:', err);
        const errorText = typeof err.response?.data === 'string'
          ? err.response.data
          : err.response?.data?.message || 'Не удалось отклонить претензию';

        this.showError(errorText);
      } finally {
        await this.loadClaimData();
        this.clearProcessing(item.id);
      }
    },

    // Подтверждение отклонения
    confirmReject(item) {
      const message = item.goodName
        ? `Вы уверены, что хотите отклонить претензию по товару "${item.goodName}"?`
        : 'Вы уверены, что хотите отклонить эту претензию?';

      return confirm(message);
    },

    // Управление состоянием обработки
    setProcessing(itemId) {
      this.processingItems.push(itemId);
    },

    clearProcessing(itemId) {
      const index = this.processingItems.indexOf(itemId);
      if (index !== -1) {
        this.processingItems.splice(index, 1);
      }
    },

    isProcessing(itemId) {
      return this.processingItems.includes(itemId);
    },

    // Форматирование
    formatDate(dateString) {
      if (!dateString) return '';

      if (typeof dateString === 'string' && dateString.includes('T')) {
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('ru-RU');
        }
      }

      if (dateString instanceof Date) {
        if (isNaN(dateString.getTime())) return '';
        return dateString.toLocaleDateString('ru-RU');
      }

      return '';
    },

    formatPrice(price) {
      if (price == null || isNaN(price)) return '0.00';
      return Number(price).toFixed(2);
    },

    // Статусы
    getClaimStatusColor(status) {
      const statusColors = {
        0: 'orange',
        1: 'green',
        2: 'red',
        3: 'blue',
        4: 'purple'
      };
      return statusColors[status] || 'grey';
    },

    getClaimStatusName(status) {
      const statusNames = {
        0: 'В обработке',
        1: 'Одобрена',
        2: 'Отклонена',
        3: 'Завершена',
        4: 'Отменена'
      };
      return statusNames[status] || `Статус ${status}`;
    },

    // Уведомления
    showSuccess(message) {
      this.$notify({
        type: 'success',
        title: 'Успешно',
        text: message
      });
    },

    showError(message) {
      this.$notify({
        type: 'error',
        title: 'Ошибка',
        text: message
      });
    },

    showWarning(message) {
      this.$notify({
        type: 'warning',
        title: 'Внимание',
        text: message
      });
    }
  }
};
</script>

<style scoped>
.tableHeader {
  background-color: #f5f5f5;
}

.tableHeaderText--text {
  color: #333;
}
</style>